import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet } from "lucide-react";
import ExportExcelFechasModal from "@/components/ExportExcelFechasModal";
import { toast } from "sonner";

/**
 * ExcelExportButton — botón universal que abre el modal `ExportExcelFechasModal`
 * y descarga el xlsx del endpoint indicado.
 *
 * Props:
 *   endpoint   — path relativo al backend (ej: "/reportes/rentabilidad/excel")
 *   filename   — base del filename descargado (sin extension). Se le concatena
 *                `_desde_hasta.xlsx` automáticamente.
 *   label      — texto del botón. Default: "Excel del periodo".
 *   variant    — variante del botón shadcn. Default: "outline".
 *   size       — tamaño. Default: "default".
 *   extraParams — objeto adicional que se agrega a la URL como query string.
 *   apiClient  — cliente axios (por defecto se importa desde @/lib/api).
 *   testid     — data-testid del botón (default: "excel-export-btn").
 *
 * Uso:
 *   <ExcelExportButton endpoint="/socios/excel" filename="socios" />
 */
import { api as defaultApi } from "@/lib/api";

export default function ExcelExportButton({
  endpoint,
  filename = "reporte",
  label = "Excel del periodo",
  variant = "outline",
  size = "default",
  className = "",
  extraParams = {},
  apiClient = defaultApi,
  testid = "excel-export-btn",
}) {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const descargar = async ({ desde, hasta }) => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (desde) params.set("desde", desde);
      if (hasta) params.set("hasta", hasta);
      Object.entries(extraParams || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
      });
      const url = `${endpoint}${params.toString() ? `?${params.toString()}` : ""}`;
      const res = await apiClient.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: res.headers["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const href = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      const suffix = `${desde || "inicio"}_${hasta || "hoy"}`;
      a.download = `${filename}_${suffix}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(href);
      toast.success("Excel descargado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo exportar");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        disabled={downloading}
        className={`gap-2 ${className}`}
        data-testid={testid}
      >
        <FileSpreadsheet className="w-4 h-4" />
        {downloading ? "Descargando..." : label}
      </Button>
      <ExportExcelFechasModal
        open={open}
        onOpenChange={setOpen}
        onExportar={descargar}
        titulo={label}
      />
    </>
  );
}
